import { useRef, useState } from "react";
import { Upload, Trash2, Download, ClipboardList, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE, getToken, formatApiError } from "@/lib/api";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");

function ReportUploader({ client, onUploaded }) {
  const ref = useRef(null);
  const [label, setLabel] = useState(new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" }) + " report");
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("user_id", client.id);
    fd.append("label", label);
    try {
      const { data } = await api.post("/admin/reports", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(client.id, data.report);
      toast.success(`Report added to @${client.username}'s account.`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 mt-4">
      <input className="field !py-2 !px-3 !text-sm flex-1 min-w-[180px]" value={label} onChange={(e) => setLabel(e.target.value)} data-testid="report-label-input" />
      <input ref={ref} type="file" accept="application/pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="report-file-input" />
      <button onClick={() => ref.current?.click()} disabled={busy} className="btn-primary !py-2 !px-4" data-testid="report-upload-btn">
        <Upload size={13} /> {busy ? "Uploading…" : "Upload PDF"}
      </button>
    </div>
  );
}

export default function AdminClients({ clients, setClients }) {
  const [openId, setOpenId] = useState(null);
  const onUploaded = (id, report) => setClients((l) => l.map((c) => (c.id === id ? { ...c, reports: [report, ...c.reports] } : c)));
  const remove = async (client, report) => {
    if (!window.confirm(`Remove “${report.label}”?`)) return;
    try {
      await api.delete(`/admin/reports/${report.id}`);
      setClients((l) => l.map((c) => (c.id === client.id ? { ...c, reports: c.reports.filter((r) => r.id !== report.id) } : c)));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!clients.length) return <p className="text-sm text-[#8C7562]">No paying clients yet — they'll appear here after their first checkout.</p>;

  return (
    <div className="flex flex-col gap-4" data-testid="admin-clients-list">
      {clients.map((c) => (
        <article key={c.id} className="glass rounded-2xl p-6" data-testid="admin-client-card">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h3 className="f-display text-2xl">@{c.username}</h3>
              {c.email && <p className="text-sm text-[#8C7562]">{c.email}</p>}
              <div className="flex flex-wrap gap-2 mt-2">
                {c.social_plan && <span className="text-[11px] font-semibold border border-[#A8785A] text-[#A8785A] rounded-full px-3 py-1">Social · {c.social_plan.name} · {c.social_plan.status}</span>}
                {(c.website_orders || []).map((o) => <span key={o.session_id} className="text-[11px] font-semibold border border-[#E8DCCB] bg-[#FFF3F8] rounded-full px-3 py-1">Website · {o.name}</span>)}
                <span className={`text-[11px] font-semibold rounded-full px-3 py-1 ${c.onboarding ? "bg-[#3A2E25] text-[#FFFDF7]" : "border border-[#C48A9F] text-[#C48A9F]"}`} data-testid="client-onboarding-status">
                  {c.onboarding ? "Brand assets received" : "Awaiting brand assets"}
                </span>
              </div>
            </div>
            <button onClick={() => setOpenId(openId === c.id ? null : c.id)} className="btn-ghost !py-2 !px-4" data-testid="client-toggle-btn">
              <ChevronDown size={13} className={`transition-transform ${openId === c.id ? "rotate-180" : ""}`} /> {openId === c.id ? "Hide" : "Details"}
            </button>
          </div>

          {openId === c.id && (
            <div className="grid lg:grid-cols-2 gap-6 mt-6 pt-6 border-t border-[#E8DCCB]" data-testid="client-details">
              <div>
                <p className="eyebrow flex items-center gap-1.5 mb-3"><ClipboardList size={12} /> Brand assets</p>
                {c.onboarding ? (
                  <dl className="text-sm flex flex-col gap-2">
                    {c.has_onboarding_logo && <img src={`${API_BASE}/account/onboarding/logo?user_id=${c.id}&auth=${getToken()}`} alt="Client logo" className="w-20 h-20 object-contain rounded-lg border border-[#E8DCCB] bg-[#FFFDF7] p-1.5 mb-2" />}
                    {Object.entries(c.onboarding).filter(([k, v]) => k !== "submitted_at" && v).map(([k, v]) => (
                      <div key={k}><dt className="text-[10px] uppercase tracking-[0.16em] text-[#8C7562]">{k.replace(/_/g, " ")}</dt><dd className="text-[#57473B] whitespace-pre-wrap">{v}</dd></div>
                    ))}
                  </dl>
                ) : <p className="text-sm text-[#8C7562]">Not submitted yet.</p>}
              </div>
              <div>
                <p className="eyebrow mb-3">Monthly reports</p>
                {!c.reports.length && <p className="text-sm text-[#8C7562]">No reports uploaded yet.</p>}
                <ul className="flex flex-col divide-y divide-[#E8DCCB]">
                  {c.reports.map((r) => (
                    <li key={r.id} className="py-2.5 flex items-center justify-between gap-3 text-sm" data-testid="client-report-row">
                      <div><p className="font-semibold">{r.label}</p><p className="text-[11px] text-[#8C7562]">{fmtDate(r.uploaded_at)} · {r.filename}</p></div>
                      <div className="flex gap-1.5">
                        <a href={`${API_BASE}/reports/${r.id}/download?auth=${getToken()}`} className="w-8 h-8 rounded-full border border-[#E8DCCB] flex items-center justify-center text-[#8C7562] hover:text-[#3A2E25]" aria-label="Download"><Download size={13} /></a>
                        <button onClick={() => remove(c, r)} className="w-8 h-8 rounded-full border border-[#E8DCCB] flex items-center justify-center text-[#8C7562] hover:text-[#C48A9F]" aria-label="Remove" data-testid="client-report-delete"><Trash2 size={13} /></button>
                      </div>
                    </li>
                  ))}
                </ul>
                <ReportUploader client={c} onUploaded={onUploaded} />
              </div>
            </div>
          )}
        </article>
      ))}
    </div>
  );
}
